import React, { Dispatch, SetStateAction } from "react"
import { WithAuth } from "../../Auth"
import { PermissionGroups } from "../../../routes/constants"
import FallbackOrderAction from "../../Order/ManageOrder/FallbackOrderAction"
import {
  ActionContainer,
  ActionWrapper,
  ButtonWrapper,
  Container,
  GiftCardInputWrapper,
  InputContainer,
  InputWrapper,
  LabelWrapper,
  StyledActionDropdownButton
} from "../../Order/ManageOrder/DeliverOrder/Cart/DeliveryAction.styled"
import ActionInfoBox from "./ActionInfoBox"
import { ActionDropdownButton } from "../../Ui/Buttons/ActionDropdownButton"
import {
  InputActionBonus,
  InputActionGiftCard,
  InputActionPaymentCancellation,
  InputActionPaymentCompensation,
  InputActionPaymentDelivery,
  InputActionPaymentOrderModificationOrderLines,
  InputActionPaymentOrderModificationShippingFees,
  InputActionPaymentRefund,
  InputActionPaymentRelease,
  InputActionShipping,
  InputActionType,
  OrderCancellation,
  OrderCompensation,
  OrderDelivery,
  OrderModificationOrderLines,
  OrderModificationShippingFees,
  OrderRefund,
  OrderRelease
} from "lib/types/generated/graphql-types"
import Money from "../../Money"
import DineroFactory from "dinero.js"
import Input from "../../Ui/Form/Input"
import PrimaryButton from "../../Ui/Buttons/PrimaryButton"
import { ReactComponent as GiftCardIcon } from "images/icons/gift.svg"
import { ReactComponent as BonusIcon } from "images/icons/hand-holding-dollar.svg"
import { ReactComponent as PaymentIcon } from "images/icons/credit-card.svg"
import { ReactComponent as ShippingIcon } from "images/icons/cart-flatbed-boxes.svg"

type ActionState = {
  giftCardAction: InputActionGiftCard
  setGiftCardAction: Dispatch<SetStateAction<InputActionGiftCard>>
  bonusAction: InputActionBonus
  setBonusAction: Dispatch<SetStateAction<InputActionBonus>>
  paymentAction:
    | InputActionPaymentDelivery
    | InputActionPaymentCompensation
    | InputActionPaymentRefund
    | InputActionPaymentRelease
    | InputActionPaymentCancellation
    | InputActionPaymentOrderModificationOrderLines
    | InputActionPaymentOrderModificationShippingFees
  setPaymentAction: Dispatch<
    SetStateAction<
      | InputActionPaymentDelivery
      | InputActionPaymentCompensation
      | InputActionPaymentRefund
      | InputActionPaymentRelease
      | InputActionPaymentCancellation
      | InputActionPaymentOrderModificationOrderLines
      | InputActionPaymentOrderModificationShippingFees
    >
  >
  shippingAction?: InputActionShipping
  setShippingAction?: Dispatch<SetStateAction<InputActionShipping>>
}

type Props = {
  operation:
    | OrderDelivery
    | OrderCompensation
    | OrderRefund
    | OrderRelease
    | OrderCancellation
    | OrderModificationOrderLines
    | OrderModificationShippingFees
    | undefined
  actionState: ActionState
  startOperation: () => void
  loading: boolean
}

const StartOperation = ({ operation, actionState, startOperation, loading }: Props) => {
  const {
    giftCardAction,
    setGiftCardAction,
    bonusAction,
    setBonusAction,
    paymentAction,
    setPaymentAction,
    shippingAction,
    setShippingAction
  } = actionState

  const updateGiftCardActionCards = (id: string, value: string) => {
    const idExists = giftCardAction?.giftCards?.find((giftCard) => giftCard.giftCardId === id)
    if (idExists) {
      return setGiftCardAction({
        ...giftCardAction,
        giftCards: giftCardAction?.giftCards?.map((giftCard) =>
          giftCard.giftCardId === id
            ? {
                giftCardId: id,
                transactionId: value
              }
            : giftCard
        )
      })
    }
    return setGiftCardAction({
      ...giftCardAction,
      giftCards: [...(giftCardAction.giftCards ?? []), { giftCardId: id, transactionId: value }]
    })
  }

  const updatePaymentCaptureReference = (reference: string) => {
    setPaymentAction({ ...paymentAction, captureReference: reference })
  }

  const updateShippingAction = (key: string, value: string) => {
    setShippingAction && shippingAction && setShippingAction({ ...shippingAction, [key]: value })
  }

  const capitalize = (s: string) => s && s[0].toUpperCase() + s.slice(1)
  const getLabel = (value: string) => capitalize(value.toLowerCase())

  type OperationType =
    | OrderDelivery
    | OrderCompensation
    | OrderRefund
    | OrderRelease
    | OrderCancellation
    | OrderModificationOrderLines
    | OrderModificationShippingFees
    | undefined

  const isDeliveryOperation = (op: OperationType): op is OrderDelivery => {
    return op?.__typename === "OrderDelivery"
  }

  const hasGiftCards = (
    op: OperationType
  ): op is OrderDelivery | OrderCompensation | OrderRefund | OrderRelease => {
    return op !== undefined && "giftCards" in op && op.giftCards !== undefined
  }

  const hasBonus = (
    op: OperationType
  ): op is OrderDelivery | OrderCompensation | OrderRefund | OrderRelease => {
    return op !== undefined && "bonus" in op && op.bonus !== undefined
  }

  return (
    <>
      <WithAuth requiredGroups={PermissionGroups.orders.execute} fallback={<FallbackOrderAction />}>
        <Container>
          <ActionInfoBox />
          <div>
            {hasGiftCards(operation) && operation.giftCards && operation.giftCards.length > 0 && (
              <div>
                <ActionContainer>
                  <ActionWrapper>
                    <LabelWrapper>
                      <GiftCardIcon />
                      <b>GiftCard - {operation?.giftCardProvider?.providerName}</b>
                    </LabelWrapper>
                    <ActionDropdownButton title={getLabel(giftCardAction.actionType)}>
                      <li onClick={() => setGiftCardAction({ actionType: InputActionType.Auto })}>
                        Auto
                      </li>
                      <li onClick={() => setGiftCardAction({ actionType: InputActionType.Manual })}>
                        Manual
                      </li>
                      <li onClick={() => setGiftCardAction({ actionType: InputActionType.Skip })}>
                        Skip
                      </li>
                    </ActionDropdownButton>
                  </ActionWrapper>
                  {giftCardAction.actionType === InputActionType.Manual && operation.giftCards && (
                    <>
                      {operation.giftCards.map((giftCard) => {
                        const giftCardHasAmount = "amount" in giftCard && "currencyCode" in giftCard
                        return (
                          <GiftCardInputWrapper key={giftCard.giftCardId}>
                            <p>
                              {giftCard.giftCardId}
                              {giftCardHasAmount && (
                                <>
                                  {" "}
                                  -{" "}
                                  <Money
                                    amount={giftCard.amount}
                                    currencyUnit={giftCard.currencyCode as DineroFactory.Currency}
                                  />
                                </>
                              )}
                            </p>
                            <Input
                              removePaddingBottom={true}
                              name="transactionId"
                              placeholder="Transaction id"
                              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                                updateGiftCardActionCards(
                                  giftCard.giftCardId,
                                  e.currentTarget.value
                                )
                              }
                            />
                          </GiftCardInputWrapper>
                        )
                      })}
                    </>
                  )}
                </ActionContainer>
              </div>
            )}
            {hasBonus(operation) && (
              <div>
                <ActionContainer>
                  <ActionWrapper>
                    <LabelWrapper>
                      <BonusIcon />
                      <b>Bonus - {operation?.bonusProvider?.providerName}</b>{" "}
                    </LabelWrapper>
                    <ActionDropdownButton title={getLabel(bonusAction.actionType)}>
                      <li onClick={() => setBonusAction({ actionType: InputActionType.Auto })}>
                        Auto
                      </li>
                      <li onClick={() => setBonusAction({ actionType: InputActionType.Manual })}>
                        Manual
                      </li>
                      <li onClick={() => setBonusAction({ actionType: InputActionType.Skip })}>
                        Skip
                      </li>
                    </ActionDropdownButton>
                  </ActionWrapper>
                </ActionContainer>
              </div>
            )}
            {isDeliveryOperation(operation) &&
              operation.shippingFees &&
              operation.shippingFees.length > 0 && (
                <ActionContainer>
                  <ActionWrapper>
                    <LabelWrapper>
                      <ShippingIcon />
                      <b>Shipping - {operation?.shippingProvider?.providerName}</b>
                    </LabelWrapper>
                    <StyledActionDropdownButton title={getLabel(shippingAction?.actionType ?? "")}>
                      <li
                        onClick={() =>
                          setShippingAction &&
                          setShippingAction({ actionType: InputActionType.Auto })
                        }
                      >
                        Auto
                      </li>
                      <li
                        onClick={() =>
                          setShippingAction &&
                          setShippingAction({ actionType: InputActionType.Manual })
                        }
                      >
                        Manual
                      </li>
                      <li
                        onClick={() =>
                          setShippingAction &&
                          setShippingAction({ actionType: InputActionType.Skip })
                        }
                      >
                        Skip
                      </li>
                    </StyledActionDropdownButton>
                  </ActionWrapper>
                  {shippingAction && shippingAction.actionType === InputActionType.Manual && (
                    <InputContainer>
                      <InputWrapper>
                        <Input
                          name="shippingCompany"
                          placeholder="Shipping Company"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateShippingAction("shippingCompany", e.currentTarget.value)
                          }
                        />
                        <Input
                          name="shippingMethod"
                          placeholder="Shipping Method"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateShippingAction("shippingMethod", e.currentTarget.value)
                          }
                        />
                      </InputWrapper>
                      <Input
                        name="trackingReference"
                        placeholder="Tracking reference"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateShippingAction("trackingReference", e.currentTarget.value)
                        }
                      />
                      <Input
                        name="trackingUrl"
                        placeholder="Tracking url"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateShippingAction("trackingUrl", e.currentTarget.value)
                        }
                      />
                    </InputContainer>
                  )}
                </ActionContainer>
              )}
            <ActionContainer>
              <ActionWrapper>
                <LabelWrapper>
                  <PaymentIcon />
                  <b>Payment - {operation?.paymentProvider?.providerName}</b>
                </LabelWrapper>
                <StyledActionDropdownButton title={getLabel(paymentAction.actionType)}>
                  <li onClick={() => setPaymentAction({ actionType: InputActionType.Auto })}>
                    Auto
                  </li>
                  <li onClick={() => setPaymentAction({ actionType: InputActionType.Manual })}>
                    Manual
                  </li>
                  <li onClick={() => setPaymentAction({ actionType: InputActionType.Skip })}>
                    Skip
                  </li>
                </StyledActionDropdownButton>
              </ActionWrapper>
              {paymentAction.actionType === InputActionType.Manual && (
                <InputContainer>
                  <Input
                    name="captureReference"
                    placeholder="Capture reference"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updatePaymentCaptureReference(e.currentTarget.value)
                    }
                  />
                </InputContainer>
              )}
            </ActionContainer>
          </div>
          <ButtonWrapper>
            <PrimaryButton handleClick={startOperation} loading={loading}>
              Finalize
            </PrimaryButton>
          </ButtonWrapper>
        </Container>
      </WithAuth>
    </>
  )
}

export default StartOperation
